
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useClientDelete } from "@/hooks/useClientDelete";
import { DeleteClientDialog } from "./DeleteClientDialog";
import type { Client } from "@/types";

interface ClientsTableProps {
  clients: Client[];
  isLoading: boolean;
  onClientRemoved?: (clientId: string) => void;
}

export function ClientsTable({ clients, isLoading, onClientRemoved }: ClientsTableProps) {
  const navigate = useNavigate();
  const { isDeleting, error, deleteClient } = useClientDelete();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  if (isLoading) {
    return <div className="p-8 text-center text-white/70">Loading...</div>;
  }
  
  if (clients.length === 0) {
    return null;
  }

  const handleRowClick = (client: Client, event: React.MouseEvent) => {
    // Prevent navigation when clicking the delete button
    if ((event.target as HTMLElement).closest('.delete-button')) {
      event.stopPropagation();
      return;
    }
    navigate(`/clients/${client.id}`);
  };

  const handleDeleteClick = (client: Client, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedClient(client);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedClient) {
      const success = await deleteClient(selectedClient.id);
      
      // Only close dialog and notify parent if deletion was successful
      if (success && onClientRemoved) {
        setIsDeleteDialogOpen(false);
        onClientRemoved(selectedClient.id);
      }
    }
  };
  
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-white/5 border-white/20">
            <TableHead className="text-white/70">Client</TableHead>
            <TableHead className="text-white/70">Date Joined</TableHead>
            <TableHead className="text-white/70">Status</TableHead>
            <TableHead className="text-white/70 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map(client => (
            <TableRow 
              key={client.id} 
              className="hover:bg-white/5 border-white/20 cursor-pointer" 
              onClick={(event) => handleRowClick(client, event)}
            >
              <TableCell>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {client.avatar && client.avatar !== "/placeholder.svg" && (
                      <AvatarImage src={client.avatar} alt={client.name} />
                    )}
                    <AvatarFallback>
                      {client.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium text-white">{client.name}</div>
                    <div className="text-sm text-white/70">{client.email}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-white">
                <div className="text-sm text-white/70">
                  Joined {new Date(client.joinDate).toLocaleDateString()}
                </div>
              </TableCell>
              <TableCell>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-white/10 text-white">
                  {client.status}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white/70 hover:text-destructive hover:bg-white/10 delete-button"
                  onClick={(event) => handleDeleteClick(client, event)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {selectedClient && (
        <DeleteClientDialog
          clientName={selectedClient.name}
          isOpen={isDeleteDialogOpen}
          isDeleting={isDeleting}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          error={error}
        />
      )}
    </>
  );
}

